/**
 * Loads the SituReach library on first use, never up front.
 *
 * Importing SituReach injects its stylesheets into the whole document. When the
 * portal imported it statically, it landed in vendor.js and ran on every page,
 * including situLINK share pages, where SituView brings its own copy. Two
 * copies of the showcase CSS on one page means the portal's version can
 * override SituView's (that is how a stale portal broke the short-host layout
 * on share pages). A dynamic import keeps SituReach in its own chunk, loaded
 * only by the portal pages that actually render tiles or the showcase.
 *
 * Type-only imports (`import type ... from 'situsystems-situreach'`) are fine
 * anywhere: they are erased at compile time and pull nothing in.
 */
type SituReachModule = typeof import('situsystems-situreach');

let loaded: SituReachModule | null = null;
let loading: Promise<SituReachModule> | null = null;

export function loadSituReach(): Promise<SituReachModule> {
  if (loaded) {
    return Promise.resolve(loaded);
  }
  if (!loading) {
    loading = import('situsystems-situreach').then((mod) => {
      // Point the lib at this environment's SituView API; it defaults to
      // production otherwise. AppConfig is loaded at bootstrap.
      const situViewBaseApi = (window as any).appconfig?.situViewWebApiBaseUrl;
      if (situViewBaseApi) {
        mod.configure({ situViewBaseApi });
      }
      loaded = mod;
      return mod;
    });
  }
  return loading;
}

/** The library if something has already loaded it, without loading it. */
export function situReachIfLoaded(): SituReachModule | null {
  return loaded;
}
